import { faExclamationCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as qs from 'query-string';
import React, { useEffect, useState } from 'react';
import axios from '../../../axios';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';

import Button from '../../../components/UI/Button/Button';
import Container from '../../../components/UI/Container/Container';
import  { EInputType, IInputField } from '../../../components/UI/Input/Input';
import { useInputs } from '../../../components/UI/Input/useInputs';
import InputGroup from '../../../components/UI/InputGroup/InputGroup';
import Spinner from '../../../components/UI/Spinner/Spinner';
import { EInputUpdateAction } from '../../../context/InputContext';
import { buildingTypes, languages } from '../../../data/select-data';
import { EBuildingType, EOfferRequestType } from '../../../enums';
import { IAppState, IOfferRequest, IOfferRequestState } from '../../../interfaces';
import { formatDateString, initForm, parseDate } from '../../../shared/utility';
import * as actions from '../../../store/actions';
import { ETranslation } from '../../../translations/translation-keys';
import Feedback from '../../Feedbacks/Feedback/Feedback';
import classes from './CustomerOfferRequest.module.scss';
import { getDynamicFieldsForService } from '../../../config/forms';

interface IProps extends RouteComponentProps {}

export enum ECustomerOfferRequest {
  created = "created",
  firstName = "firstName",
  lastName = "lastName",
  address = "address",
  phone = "phone",
  email = "email",
  service = "service",
  area = "area",
  buildingType = "buildingType",
  buildingTypeOther = "buildingTypeOther",
  constructionYear = "constructionYear",
  zip = "zip",
  city = "city",
  additionalInfo = "additionalInfo",
  province = "province",
  municipality = "municipality",
  language = "language"
}

const getBrandTheme = (service?: any) => {
  return {
    primaryColor: service?.brandColor || '#E2512C', // Default Orange
    brandName: service?.name || 'HankiX',
    heroImage: service?.heroImage
  };
};

const mapBuildingTypeToEnum = (value: string): string => {
  if (!value) return "";
  const lowerVal = value.toLowerCase().trim();
  if (lowerVal === "kerrostalo" || lowerVal === "asuinkerrostalo" || lowerVal === "apartmenthouse" || lowerVal === "apartment") {
    return "apartmentHouse";
  }
  if (lowerVal === "omakotitalo" || lowerVal === "detachedhouse" || lowerVal === "detached" || lowerVal === "hirsitalo") {
    return "detachedHouse";
  }
  if (lowerVal === "rivitalo" || lowerVal === "rowhouse" || lowerVal === "row") {
    return "rowHouse";
  }
  if (lowerVal === "paritalo" || lowerVal === "semidetachedhouse" || lowerVal === "semidetached" || lowerVal === "ketjutalo") {
    return "semidetachedHouse";
  }
  if (lowerVal === "kaupunkipientalo" || lowerVal === "townhouse" || lowerVal === "town_house") {
    return "townHouse";
  }
  if (lowerVal === "muu" || lowerVal === "other" || lowerVal === "muu rakennus") {
    return "other";
  }
  return value;
};

const CustomerOfferRequest: React.FC<IProps> = ({ location, history }) => {
  const dispatch = useDispatch();
  const queryParams = React.useMemo(() => qs.parse(location.search), [location.search]);
  const pin = (queryParams.pin as string) || "";
  const email = (queryParams.email as string) || "";
  const accessKey = (queryParams.accessKey as string) || "";
  const hasCredentials = Boolean((pin && email) || accessKey);

  const [isEditing, setIsEditing] = useState(false);
  const [showToast, setShowToast] = useState(false);

  const [signalingLoading, setSignalingLoading] = useState(false);
  const [signalSentAt, setSignalSentAt] = useState<string | null>(null);

  const { loading, error, offerRequest } = useSelector<IAppState, IOfferRequestState>(state => state.offerRequest);

  useEffect(() => {
    if (offerRequest?.moreOffersRequestedAt) {
      setSignalSentAt(offerRequest.moreOffersRequestedAt);
    }
  }, [offerRequest]);

  const handleSignalMoreOffers = async (e: React.MouseEvent) => {
    e.preventDefault();
    if (!offerRequest?.id) return;
    setSignalingLoading(true);
    try {
      const res = await axios.post(`/json/offerrequests/signal-more?id=${offerRequest.id}`, {});
      if (res.data && res.data.moreOffersRequestedAt) {
        setSignalSentAt(res.data.moreOffersRequestedAt);
      }
    } catch (err) {
      console.error("Signal more offers error:", err);
    } finally {
      setSignalingLoading(false);
    }
  };

  const { t } = useTranslation();

  const dynamicFieldsDef = React.useMemo(() => getDynamicFieldsForService(offerRequest?.service?.serviceToken, offerRequest?.service?.type), [offerRequest?.service]);

  const [inputs, setInputs] = useState<IInputField>({
    [ECustomerOfferRequest.created]: {
      type: EInputType.static,
      labelTranslation: ETranslation.COMMON_CREATED,
      value: "",
    },
    [ECustomerOfferRequest.firstName]: {
      type: EInputType.text,
      labelTranslation: ETranslation.OFFERREQUEST_FIRSTNAME,
      placeholderTranslation: ETranslation.OFFERREQUEST_FIRSTNAME,
      value: "",
    },
    [ECustomerOfferRequest.lastName]: {
      type: EInputType.text,
      labelTranslation: ETranslation.OFFERREQUEST_LASTNAME,
      placeholderTranslation: ETranslation.OFFERREQUEST_LASTNAME,
      value: "",
    },
    [ECustomerOfferRequest.address]: {
      type: EInputType.text,
      labelTranslation: ETranslation.COMMON_STREET_ADDRESS,
      placeholderTranslation: ETranslation.COMMON_STREET_ADDRESS,
      value: "",
    },
    [ECustomerOfferRequest.phone]: {
      type: EInputType.text,
      labelTranslation: ETranslation.COMMON_PHONE,
      placeholderTranslation: ETranslation.COMMON_PHONE,
      value: "",
      validation: {
        required: true
      }
    },
    [ECustomerOfferRequest.email]: {
      type: EInputType.text,
      labelTranslation: ETranslation.COMMON_EMAIL,
      placeholderTranslation: ETranslation.COMMON_EMAIL,
      value: "",
      disabled: true,
    },
    [ECustomerOfferRequest.service]: {
      type: EInputType.static,
      labelTranslation: ETranslation.COMMON_SERVICE,
      placeholderTranslation: ETranslation.COMMON_SERVICE,
      value: "",
    },
    [ECustomerOfferRequest.area]: {
      type: EInputType.text,
      labelTranslation: ETranslation.OFFER_REQUEST_CONSTRUCTION_AREA,
      placeholderTranslation: ETranslation.OFFER_REQUEST_CONSTRUCTION_AREA,
      value: "",
    },
    [ECustomerOfferRequest.buildingType]: {
      type: EInputType.reactSelect,
      labelTranslation: ETranslation.OFFER_REQUEST_CONSTRUCTION_BUILDING_TYPE,
      placeholderTranslation: ETranslation.OFFER_REQUEST_CONSTRUCTION_BUILDING_TYPE,
      options: buildingTypes,
      value: EBuildingType.TOWN_HOUSE,
    },
    [ECustomerOfferRequest.buildingTypeOther]: {
      type: EInputType.text,
      labelTranslation:
        ETranslation.OFFER_REQUEST_CONSTRUCTION_BUILDING_TYPE_OTHER,
      placeholderTranslation:
        ETranslation.OFFER_REQUEST_CONSTRUCTION_BUILDING_TYPE_OTHER,
      value: "",
    },
    [ECustomerOfferRequest.constructionYear]: {
      type: EInputType.text,
      labelTranslation:
        ETranslation.OFFER_REQUEST_CONSTRUCTION_CONSTRUCTION_YEAR,
      placeholderTranslation:
        ETranslation.OFFER_REQUEST_CONSTRUCTION_CONSTRUCTION_YEAR,
      value: "",
    },
    [ECustomerOfferRequest.zip]: {
      type: EInputType.text,
      labelTranslation: ETranslation.COMMON_ZIP,
      placeholderTranslation: ETranslation.COMMON_ZIP,
      value: "",
    },
    [ECustomerOfferRequest.city]: {
      type: EInputType.text,
      labelTranslation: ETranslation.COMMON_CITY,
      placeholderTranslation: ETranslation.COMMON_CITY,
      value: "",
    },
    [ECustomerOfferRequest.additionalInfo]: {
      type: EInputType.textarea,
      labelTranslation: ETranslation.OFFERREQUEST_INFO,
      placeholderTranslation: ETranslation.OFFERREQUEST_INFO,
      value: "",
    },
    [ECustomerOfferRequest.province]: {
      type: EInputType.text,
      labelTranslation: ETranslation.OFFER_REQUEST_PROVINCE,
      placeholderTranslation: ETranslation.OFFER_REQUEST_PROVINCE,
      value: "",
    },
    [ECustomerOfferRequest.municipality]: {
      type: EInputType.text,
      labelTranslation: ETranslation.OFFER_REQUEST_MUNICIPALITY,
      placeholderTranslation: ETranslation.OFFER_REQUEST_MUNICIPALITY,
      value: "",
    },
    [ECustomerOfferRequest.language]: {
      type: EInputType.reactSelect,
      labelTranslation: ETranslation.OFFER_REQUEST_LANGUAGE,
      placeholderTranslation: ETranslation.OFFER_REQUEST_LANGUAGE,
      value: "",
      options: []
    },
  });

  useEffect(() => {
    if ((pin && email) || accessKey) {
      dispatch(actions.getCustomerOfferRequest(email, pin, accessKey));
    } 
  }, [dispatch, pin, email, accessKey]);

  useEffect(() => {
    if (offerRequest) {
      const formattedOfferRequest = { ...offerRequest };
      const originalCreated = formattedOfferRequest.created;
      if (formattedOfferRequest.dynamicFields) {
        Object.assign(formattedOfferRequest, formattedOfferRequest.dynamicFields);
      }
      if (originalCreated) {
        formattedOfferRequest.created = originalCreated;
      }
      if (formattedOfferRequest.buildingType) {
        formattedOfferRequest.buildingType = mapBuildingTypeToEnum(formattedOfferRequest.buildingType);
      }
      formattedOfferRequest.created = formatDateString(formattedOfferRequest.created);

      setInputs((prev) => {
        const newInputs = { ...prev };
        dynamicFieldsDef.forEach(df => {
          if (!newInputs[df.name as any]) {
            newInputs[df.name as any] = {
              type: df.type,
              label: df.label,
              labelTranslation: df.labelTranslation,
              value: "",
              options: df.options
            }
          } else {
            newInputs[df.name as any].label = df.label;
            newInputs[df.name as any].type = df.type;
            newInputs[df.name as any].labelTranslation = df.labelTranslation;
            newInputs[df.name as any].options = df.options;
          }
        });
        return newInputs;
      });

      initForm(setInputs, formattedOfferRequest);
    }
  }, [offerRequest, dynamicFieldsDef]);

  const { createInput, submit } = useInputs({
    inputs,
    setInputs,
    options: {
      updateAction: EInputUpdateAction.OFFERREQUEST,
      disabled: offerRequest?.archived,
    }
  });

  const submitHandler = async (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    event.preventDefault();

    const offerRequestSubmit = await submit() as IOfferRequest;
    if (!offerRequestSubmit) return;

    dispatch(
      actions.updateCustomerOfferRequest(
        email,
        pin,
        offerRequestSubmit,
        accessKey
      )
    );

    setIsEditing(false);

    setShowToast(true);
    setTimeout(() => {
      setShowToast(false);
    }, 3000);
  };

  const archiveHandler = async (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent> | React.MouseEvent<HTMLAnchorElement, MouseEvent>
  ) => {
    event.preventDefault();
    dispatch(
      actions.archiveCustomerOfferRequest(email, pin, accessKey)
    );
  };

  const theme = getBrandTheme(offerRequest?.service);

  return (
    <div 
      className={classes.Wrapper} 
      style={{ '--brand-primary': theme.primaryColor } as React.CSSProperties}
    >
      {theme.heroImage && (
        <div style={{ 
          width: '100%', 
          height: '200px', 
          backgroundImage: `url(${theme.heroImage})`, 
          backgroundSize: 'cover', 
          backgroundPosition: 'center',
          borderTopLeftRadius: '8px',
          borderTopRightRadius: '8px',
          marginBottom: '-2rem'
        }} />
      )}
      <div className={classes.BrandHeader} style={theme.heroImage ? { padding: '2rem 15px 1.5rem', backgroundColor: 'rgba(255,255,255,0.95)', borderTopLeftRadius: '8px', borderTopRightRadius: '8px', position: 'relative' } : {}}>
         <div className={classes.LogoText}>{theme.brandName}</div>
      </div>
      {loading ? (
        <Container>
          <Spinner />
        </Container>
      ) : error || !hasCredentials || !offerRequest ? (
        <div className={classes.Error}>
          <span className={classes.ErrorIcon}><FontAwesomeIcon icon={faExclamationCircle}/></span>
          <h2>{t(ETranslation.CUSTOMER_OFFER_REQUEST_ERROR_TOP)}</h2>
          <p>{t(ETranslation.CUSTOMER_OFFER_REQUEST_ERROR_BOTTOM)}</p>
          <a href="mailto:info@hankix.fi">info@hankix.fi</a>
        </div>
      ) : offerRequest?.archived ? (
        <Container>
          <div style={{ padding: '15px', backgroundColor: '#f9f9f9', borderRadius: '8px', marginBottom: '20px' }}>
             <h3>{t(ETranslation.CUSTOMER_OFFER_REQUEST)}</h3>
             <p style={{ margin: '10px 0 0 0', lineHeight: '1.6' }}>
               <strong>{t(ETranslation.COMMON_SERVICE)}:</strong> {offerRequest.service?.name} <br/>
               {offerRequest.service?.type !== EOfferRequestType.WELLNESS && (
                 <><strong>{t(ETranslation.COMMON_STREET_ADDRESS)}:</strong> {offerRequest.address} <br/></>
               )}
               <strong>{t(ETranslation.COMMON_CREATED)}:</strong> {offerRequest.created}
             </p>
          </div>
          <Feedback 
             location={location} 
             history={history} 
             offerRequestId={offerRequest.id?.toString()} 
             pinCode={pin} 
             authAccessKey={accessKey}
             embedded={true} 
          />
        </Container>
        ) : (
        <Container>
          <div style={{ marginBottom: '20px' }}>
             <h2 style={{ marginBottom: '5px' }}>Hei {offerRequest?.firstName}!</h2>
             <span style={{ fontSize: '1.1rem', color: '#555' }}>Tässä on tarjouspyyntösi tilanne.</span>
          </div>
          {loading ? (
            <Spinner />
          ) : offerRequest?.deleted ? (
            <p>{t(ETranslation.OFFER_REQUEST_DELETED)}</p>
          ) : !isEditing && offerRequest ? (() => {
            const createdDate = offerRequest?.created ? parseDate(offerRequest.created) : null;
            const todayStart = new Date();
            todayStart.setHours(0, 0, 0, 0);
            const isCreatedToday = createdDate ? createdDate >= todayStart : false;

            const isStage2 = !offerRequest.archived && (!isCreatedToday || (offerRequest.viewCount || 0) >= 1 || (offerRequest.offerCount || 0) >= 1);
            const isStage3 = (offerRequest.viewCount || 0) >= 1 || offerRequest.matchStatus === 'IN_VIEWING' || (offerRequest.offerCount || 0) >= 1 || offerRequest.matchStatus === 'MATCHED';
            const isStage4 = offerRequest.matchStatus === 'MATCHED' || (offerRequest.offerCount || 0) >= 3 || offerRequest.archived;

            const currentStage = isStage4 ? 4 : isStage3 ? 3 : isStage2 ? 2 : 1;

            return (
              <>
                {/* Ultra-Minimalist Text Status Line */}
                <div style={{ padding: '8px 12px', backgroundColor: '#f8fafc', borderRadius: '6px', border: '1px solid #e2e8f0', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', fontSize: '0.8rem' }}>
                  <span style={{ fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: '0.75rem', marginRight: '2px' }}>
                    Eteneminen:
                  </span>
                  {[
                    { step: 1, label: 'Vastaanotettu' },
                    { step: 2, label: 'Välitetty' },
                    { step: 3, label: 'Tarjousta laaditaan' },
                    { step: 4, label: 'Käsitelty' }
                  ].map((item, idx) => {
                    const isCurrent = item.step === currentStage;
                    const isPassed = item.step < currentStage;
                    return (
                      <React.Fragment key={item.step}>
                        {idx > 0 && <span style={{ color: '#cbd5e1', margin: '0 1px' }}>›</span>}
                        <span style={{
                          fontWeight: isCurrent ? 700 : isPassed ? 500 : 400,
                          color: isCurrent ? '#0284c7' : isPassed ? '#475569' : '#94a3b8',
                          textDecoration: isCurrent ? 'underline' : 'none',
                          textUnderlineOffset: '3px'
                        }}>
                          {item.label}
                        </span>
                      </React.Fragment>
                    );
                  })}
                </div>

              <div className={classes.SummaryCard} style={{ padding: '20px', backgroundColor: '#fff', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h3 style={{ margin: 0 }}>Tarjouspyyntösi tiedot</h3>
                <Button onClick={(e: any) => { e.preventDefault(); setIsEditing(true); }}>
                  Muokkaa
                </Button>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                <div><strong style={{ display: 'block', marginBottom: '4px' }}>{t(ETranslation.OFFERREQUEST_FIRSTNAME)}:</strong> {offerRequest.firstName}</div>
                <div><strong style={{ display: 'block', marginBottom: '4px' }}>{t(ETranslation.OFFERREQUEST_LASTNAME)}:</strong> {offerRequest.lastName}</div>
                <div style={{ gridColumn: '1 / -1' }}><strong style={{ display: 'block', marginBottom: '4px' }}>{t(ETranslation.COMMON_EMAIL)}:</strong> {offerRequest.email}</div>
                <div style={{ gridColumn: '1 / -1' }}><strong style={{ display: 'block', marginBottom: '4px' }}>{t(ETranslation.COMMON_PHONE)}:</strong> {offerRequest.phone}</div>
                
                {offerRequest.service?.type !== EOfferRequestType.WELLNESS && (
                  <>
                    <div style={{ gridColumn: '1 / -1' }}><strong style={{ display: 'block', marginBottom: '4px' }}>{t(ETranslation.COMMON_STREET_ADDRESS)}:</strong> {offerRequest.address}</div>
                    <div style={{ gridColumn: '1 / -1' }}><strong style={{ display: 'block', marginBottom: '4px' }}>{t(ETranslation.COMMON_CITY)}:</strong> {offerRequest.zip} {offerRequest.city}</div>
                  </>
                )}

                {dynamicFieldsDef
                  .filter(df => df.name !== 'acceptTerms')
                  .filter(df => !df.condition || df.condition(inputs))
                  .map(df => {
                    const val = offerRequest.dynamicFields?.[df.name];
                    if (!val) return null;
                    let displayVal = (val === "on" || val === true || val === "true") ? "Kyllä" : val;
                    if (df.name === 'buildingType') {
                      const mappedType = mapBuildingTypeToEnum(val as string);
                      let found = false;
                      
                      if (df.options) {
                        const option = df.options.find((o: any) => o.value === mappedType || o.value === val);
                        if (option) {
                          displayVal = option.labelTranslation ? t(option.labelTranslation) : (option.label || val);
                          found = true;
                        }
                      }
                      
                      if (!found) {
                        const bt = buildingTypes.find(b => b.value === mappedType);
                        if (bt?.labelTranslation) displayVal = t(bt.labelTranslation);
                        else if (bt?.label) displayVal = bt.label;
                      }
                    } else if (df.options) {
                      const option = df.options.find((o: any) => o.value === val);
                      if (option) {
                        displayVal = option.labelTranslation ? t(option.labelTranslation) : (option.label || val);
                      }
                    }
                    const labelStr = df.labelTranslation ? t(df.labelTranslation) : df.label;
                    const displayValStr = String(displayVal);
                    const suffix = df.name === 'area' && !displayValStr.endsWith('m²') ? ' m²' : '';
                    return (
                      <div key={df.name} style={df.type === 'textarea' ? { gridColumn: '1 / -1' } : {}}>
                        <strong style={{ display: 'block', marginBottom: '4px' }}>{labelStr}:</strong> {displayVal}{suffix}
                      </div>
                    );
                  })}
                
                {offerRequest.additionalInfo && (
                  <div style={{ gridColumn: '1 / -1', marginTop: '10px' }}>
                    <strong style={{ display: 'block', marginBottom: '4px' }}>{t(ETranslation.OFFERREQUEST_INFO)}:</strong>
                    <p style={{ marginTop: '5px', whiteSpace: 'pre-wrap' }}>{offerRequest.additionalInfo}</p>
                  </div>
                )}
              </div>
            </div>
            </>
            );
          })() : (
            <>
              <h3 style={{ marginBottom: '20px' }}>Muokkaa tietojasi</h3>
              <InputGroup>
                {createInput(ECustomerOfferRequest.created)}
                {createInput(ECustomerOfferRequest.service)}
              </InputGroup>
              <InputGroup>
                {createInput(ECustomerOfferRequest.firstName)}
                {createInput(ECustomerOfferRequest.lastName)}
              </InputGroup>
              {offerRequest?.service?.type !== EOfferRequestType.WELLNESS ? (
                <InputGroup>
                  {createInput(ECustomerOfferRequest.address)}
                  {createInput(ECustomerOfferRequest.zip)}
                  {createInput(ECustomerOfferRequest.city)}
                </InputGroup>
              ) : (
                <InputGroup>
                  {createInput(ECustomerOfferRequest.city)}
                  {createInput(ECustomerOfferRequest.province)}
                </InputGroup>
              )}
              <InputGroup>
                {createInput(ECustomerOfferRequest.phone)}
                {createInput(ECustomerOfferRequest.email)}
                {createInput(ECustomerOfferRequest.language, {
                  options: languages
                })}
              </InputGroup>
              {dynamicFieldsDef.length > 0 ? (
                <>
                  {dynamicFieldsDef
                    .filter((df) => df.name !== 'acceptTerms')
                    .filter((df) => !df.condition || df.condition(inputs))
                    .filter((df) => ![ECustomerOfferRequest.city, ECustomerOfferRequest.additionalInfo].includes(df.name as any))
                    .reduce((acc: any[][], curr, i) => {
                      if (i % 2 === 0) acc.push([curr]);
                      else acc[acc.length - 1].push(curr);
                      return acc;
                    }, [])
                    .map((chunk, chunkIndex) => (
                      <InputGroup key={`dynamic-chunk-${chunkIndex}`}>
                        {chunk.map((df) => (
                          <React.Fragment key={df.name}>
                            {createInput(df.name as any)}
                          </React.Fragment>
                        ))}
                      </InputGroup>
                    ))}
                </>
              ) : null}
              {createInput(ECustomerOfferRequest.additionalInfo)}
            </>
          )}
          {!offerRequest?.deleted && isEditing && (
            <>
              <div className={classes.HelpText}>
                <p>{t(ETranslation.CUSTOMER_OFFER_REQUEST_HELP_TEXT_1)}</p>
                <p>{t(ETranslation.CUSTOMER_OFFER_REQUEST_HELP_TEXT_2)}</p>
              </div>
              <InputGroup>
                <Button
                  disabled={loading || offerRequest?.archived}
                  onClick={submitHandler}
                >
                  {t(ETranslation.CUSTOMER_OFFER_REQUEST_SAVE_CHANGES)}
                </Button>
                <Button 
                  disabled={loading} 
                  onClick={(e: any) => { e.preventDefault(); setIsEditing(false); }}
                >
                  Peruuta
                </Button>
              </InputGroup>
            </>
          )}
          {!offerRequest?.deleted && !isEditing && (
             <>
               <div style={{ marginTop: '40px', padding: '20px', backgroundColor: '#fdfdfd', border: '1px solid #eee', borderRadius: '8px' }}>
                  <h4>Haluatko sulkea tarjouspyynnön?</h4>
                  <p style={{ fontSize: '0.9rem', color: '#666', marginBottom: '15px' }}>
                    Jos olet jo löytänyt tekijän tai et enää tarvitse palvelua, voit sulkea tämän tarjouspyynnön. 
                    <strong> Sulkemisen jälkeen tarjouspyyntöäsi ei enää näytetä palveluntarjoajille.</strong>
                  </p>
                  <Button disabled={loading} onClick={archiveHandler}>
                    Sulje tarjouspyyntö
                  </Button>
               </div>

               {/* "Haluatko lisää tarjouksia?" Signal Card */}
               {!offerRequest?.archived && (
                 <div style={{ marginTop: '20px', padding: '20px', backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px' }}>
                   <h4 style={{ margin: '0 0 8px 0', color: '#166534', fontSize: '1.1rem' }}>📩 Haluatko lisää tarjouksia?</h4>
                   <p style={{ margin: '0 0 15px 0', color: '#15803d', fontSize: '0.95rem', lineHeight: '1.5' }}>
                     Painamalla tätä voit viestiä palveluntarjoajille, ettet ole vielä saanut hyväksyttävää tarjousta ja toivot lisää ehdotuksia.
                   </p>
                   {signalSentAt ? (
                     <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '10px 18px', backgroundColor: '#dcfce7', color: '#15803d', border: '1px solid #86efac', borderRadius: '6px', fontWeight: 'bold' }}>
                       ⚡ Lisätarjouksia pyydetty ({formatDateString(signalSentAt)})
                     </div>
                   ) : (
                     <Button onClick={handleSignalMoreOffers} disabled={signalingLoading} style={{ backgroundColor: '#16a34a', borderColor: '#16a34a', color: '#fff', fontWeight: 'bold', padding: '10px 20px' }}>
                       {signalingLoading ? 'Lähetetään...' : '📩 Haluan lisää tarjouksia'}
                     </Button>
                   )}
                 </div>
               )}
             </>
          )}
        </Container>
      )}
      {showToast && (
        <div className={classes.Toast}>
          {t(ETranslation.CUSTOMER_OFFER_REQUEST_THANK_YOU_MESSAGE)}
        </div>
      )}
    </div>
  );
};

export default CustomerOfferRequest;
